import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import apiService from '../services/api';
import TempStart from './TempStart';
import AppShell from '../components/AppShell';
import { isClickOffer, isPlausibleOliveOfferId, isWildfireOffer } from '../utils/offerNavigation';
import { ReactComponent as StoreIcon } from '../assets/icons/store-filled.svg';
import { ReactComponent as LocationIcon } from '../assets/icons/icon-location.svg';
import { ReactComponent as XmarkIcon } from '../assets/icons/xmark.svg';
import '../styles/OfferDetail.css';

function normalizeMediaUrl(url) {
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('s3://app.gethomecrowd.com/')) {
    return url.replace('s3://app.gethomecrowd.com/', 'https://app.gethomecrowd.com/');
  }
  if (url.startsWith('s3://')) return url.replace('s3://', 'https://');
  return `https://app.gethomecrowd.com/${url.replace(/^\/+/, '')}`;
}

function normalizeHttpUrl(url) {
  if (url == null || typeof url !== 'string') return null;
  const value = url.trim();
  if (!value) return null;
  return value.startsWith('http://') || value.startsWith('https://') ? value : `https://${value}`;
}

function pickName(offer) {
  return (offer && (offer.name || offer.merchantName || offer.merchant_name)) || 'Store';
}

function pickLogo(offer) {
  if (!offer) return '';
  return (
    offer.logoUrl ||
    offer.logo ||
    offer.large_logo_url ||
    offer.small_logo_url ||
    offer.largeLogoUrl ||
    offer.smallLogoUrl ||
    ''
  );
}

function pickWildfireMerchantId(offer) {
  if (!offer) return '';
  return (
    offer.wildfireMerchantId ||
    offer.wildfire_merchant_id ||
    offer.merchantId ||
    offer.merchant_id ||
    (isWildfireOffer(offer) ? offer.offer_id || offer.offerId || '' : '') ||
    ''
  );
}

function pickOliveOfferId(offer) {
  if (!offer || isWildfireOffer(offer)) return '';
  const id = offer.offer_id || offer.offerId || '';
  return isPlausibleOliveOfferId(id) ? String(id).trim() : '';
}

function pickCategories(offer) {
  if (!offer) return '';
  if (offer.summary && String(offer.summary).trim()) {
    const first = String(offer.summary).split(/\n/)[0].trim();
    if (first.length <= 80) return first;
  }
  if (Array.isArray(offer.categories) && offer.categories.length) return offer.categories.join(', ');
  if (offer.category_name) return String(offer.category_name);
  if (offer.category) return String(offer.category);
  if (offer.shopCategoryLabel) return String(offer.shopCategoryLabel);
  const channel = String(offer.shopChannel || offer.channel || '').toLowerCase();
  if (channel === 'online' || channel === 'in_app') return 'Online shopping';
  if (isClickOffer(offer)) return 'Online shopping';
  return 'In-person shopping';
}

function truncateDescription(text, limit = 72) {
  const full = String(text || '').trim();
  if (!full) return { short: '', full: '', needsExpand: false };
  if (full.length <= limit) return { short: full, full, needsExpand: false };
  let cut = full.slice(0, limit);
  const lastSpace = cut.lastIndexOf(' ');
  if (lastSpace > Math.floor(limit * 0.5)) cut = cut.slice(0, lastSpace);
  return { short: `${cut.replace(/[.,;:\s]+$/, '')}…`, full, needsExpand: true };
}

function isNewOffer(offer) {
  return !!(offer && (offer.is_new || offer.isNew || offer.new || offer.badge === 'new'));
}

function pickExclusions(offer) {
  const list = offer?.exclusions || offer?.Exclusions || [];
  if (!Array.isArray(list)) return [];
  const seen = {};
  const out = [];
  list.forEach((item) => {
    const text = String(item || '').trim();
    if (!text || seen[text.toLowerCase()]) return;
    seen[text.toLowerCase()] = true;
    out.push(text);
  });
  return out;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function formatDaysOfWeek(days) {
  if (!days || !days.length) return 'Every day';
  const sorted = days
    .map((d) => Number(d))
    .filter((d) => Number.isFinite(d) && d >= 0 && d <= 6)
    .sort((a, b) => a - b);
  if (!sorted.length || sorted.length === 7) return 'Every day';
  if (sorted.length === 5 && sorted[0] === 1 && sorted[4] === 5) return 'Monday - Friday';
  if (sorted.length === 2 && sorted.includes(0) && sorted.includes(6)) return 'Saturday - Sunday';
  let consecutive = true;
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i] !== sorted[i - 1] + 1 && !(sorted[i - 1] === 6 && sorted[i] === 0)) {
      consecutive = false;
      break;
    }
  }
  if (consecutive && sorted.length > 2) return `${DAY_NAMES[sorted[0]]} - ${DAY_NAMES[sorted[sorted.length - 1]]}`;
  return sorted.map((d) => DAY_NAMES[d]).join(', ');
}

function formatRedemptionLimit(offer) {
  const limit = Number(offer?.redeemLimitPerUser);
  if (!limit) return 'Unlimited redemptions';
  const interval = String(offer.redeemLimitPerUserInterval || 'month').toLowerCase();
  const count = Number(offer.redeemLimitPerUserIntervalCount) || 1;
  if (interval === 'lifetime' || interval === 'offer') {
    return `${limit} redemption${limit > 1 ? 's' : ''} for this offer`;
  }
  let intervalText = interval;
  if (['day', 'week', 'month', 'year'].includes(interval)) {
    intervalText = count === 1 ? interval : `${count} ${interval}s`;
  }
  return `${limit === 1 ? '1 redemption' : `${limit} redemptions`} per ${intervalText}`;
}

function formatOfferEndDate(value) {
  if (!value) return 'No expiration date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No expiration date';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function pickOliveImportantTerms(offer) {
  const terms = [
    `Valid Days: ${formatDaysOfWeek(offer.daysAvailability)}`,
    `Redemption Limit: ${formatRedemptionLimit(offer)}`,
  ];
  if (Number(offer.budget) > 0) terms.push('Budget: Subject to merchant budget - offer may end early');
  if (Number(offer.purchaseAmount) > 0) terms.push(`Minimum Purchase: $${offer.purchaseAmount}`);
  const trigger = String(offer.redemptionTrigger || '');
  if (trigger && trigger !== 'minimum_purchase_amount') {
    let activation = '';
    if (trigger === 'cumulative_purchase_amount') activation = 'Based on cumulative purchases';
    else if (trigger === 'purchase_frequency') activation = `After ${offer.purchaseFrequency || 'multiple'} purchases`;
    else if (trigger === 'click' || trigger === 'click_sso') activation = 'Click to activate offer';
    else if (trigger === 'mobile') activation = 'Mobile activation required';
    if (activation) terms.push(`Activation: ${activation}`);
  }
  terms.push(`Offer Expires: ${formatOfferEndDate(offer.endDate)}`);
  return terms;
}

function firstStore(offer) {
  return (offer?.stores && offer.stores[0]) || null;
}

function pickStoreLatLng(offer) {
  const store = firstStore(offer);
  const lat = Number(offer?.latitude ?? offer?.lat ?? store?.latitude ?? store?.lat);
  const lng = Number(offer?.longitude ?? offer?.lng ?? store?.longitude ?? store?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function pickStreet(offer) {
  return String(offer?.address || firstStore(offer)?.address || '').trim();
}

function pickDisplayAddress(offer) {
  const store = firstStore(offer);
  const pick = (key) => offer[key] || (store && store[key]) || '';
  const region = [pick('state'), pick('postcode') || pick('postal_code') || pick('zip')].filter(Boolean).join(' ');
  return [pickStreet(offer), pick('city'), region].filter(Boolean).join(', ');
}

function shouldOpenDirections(offer) {
  if (!offer || isClickOffer(offer)) return false;
  return !!(pickStoreLatLng(offer) || pickStreet(offer));
}

function pickMapsQuery(offer) {
  const point = pickStoreLatLng(offer);
  if (point) return `${point.lat},${point.lng}`;
  const store = firstStore(offer);
  const address = pickStreet(offer);
  if (address) return address;
  const name = pickName(offer);
  const loc = [offer.city || store?.city || '', offer.state || store?.state || ''].filter(Boolean).join(', ');
  if (name && loc) return `${name} ${loc}`;
  if (loc) return loc;
  return name !== 'Store' ? name : '';
}

function initials(name) {
  return String(name || '')
    .split(/\s+/)
    .map((word) => word[0] || '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function openPending() {
  const win = window.open('', '_blank');
  if (win) win.opener = null;
  return win;
}

function finishOpen(win, url) {
  if (win && !win.closed) {
    win.location.href = url;
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

function closePending(win) {
  if (win && !win.closed) win.close();
}

function OfferDetailPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const initialOffer = useMemo(() => {
    if (location.state?.offer) return location.state.offer;
    const source = params.get('source');
    if (source === 'wildfire') {
      return { id, wildfireMerchantId: id, offerSource: 'wildfire', offer_type: 'click', offerType: 'click' };
    }
    return { id, offerId: id };
  }, [id, params, location.state]);
  const [offer, setOffer] = useState(initialOffer);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [cardLinked, setCardLinked] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiService.fetchCurrentUser().catch(() => null),
      apiService.getPaymentCards().catch(() => null),
    ]).then(([freshUser, cards]) => {
      if (cancelled) return;
      if (freshUser?.active_school?.early_release) {
        setCardLinked(true);
        return;
      }
      const raw = cards?.results || cards?.data || cards;
      setCardLinked(Array.isArray(raw) && raw.some((card) => (card?.status || 'active') === 'active'));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const wildfireHint = isWildfireOffer(initialOffer);
        const merchantIdHint = pickWildfireMerchantId(initialOffer) || (wildfireHint ? initialOffer.id : '');
        const routeId = initialOffer.offerId || initialOffer.offer_id || initialOffer.id || merchantIdHint || '';
        const canFetchById = routeId && routeId !== 'featured' && routeId !== 'unknown';
        let oliveOfferId = !wildfireHint ? initialOffer.offer_id || initialOffer.offerId || '' : '';
        if (oliveOfferId && String(oliveOfferId) === String(routeId)) oliveOfferId = '';

        const [byRoute, byOlive, wildfire] = await Promise.all([
          wildfireHint || !canFetchById ? null : apiService.getOfferDetails(routeId).catch(() => null),
          oliveOfferId ? apiService.getOfferDetails(oliveOfferId).catch(() => null) : null,
          merchantIdHint ? apiService.getWildfireMerchantDetail(merchantIdHint).catch(() => null) : null,
        ]);
        if (cancelled) return;

        const detail = wildfire?.merchant || null;
        let next = null;
        if (detail) {
          next = {
            ...initialOffer,
            ...detail,
            offer_type: 'click',
            offerType: 'click',
            offerSource: 'wildfire',
            offer_source: 'wildfire',
          };
        } else if (byRoute || byOlive) {
          next = { ...initialOffer, ...(byRoute || byOlive) };
        } else if (location.state?.offer) {
          next = initialOffer;
        }
        if (!next) {
          setLoadError('Shop not found');
          setOffer(null);
          return;
        }
        setOffer(next);
      } catch (error) {
        if (!cancelled) {
          setOffer(location.state?.offer || null);
          if (!location.state?.offer) setLoadError(error?.message || 'Failed to load shop');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialOffer, location.state]);

  const name = pickName(offer);
  const logo = normalizeMediaUrl(pickLogo(offer));
  const desc = truncateDescription(pickCategories(offer));
  const isOlive = offer ? !isWildfireOffer(offer) : false;
  const displayAddress = offer && !isClickOffer(offer) ? pickDisplayAddress(offer) : '';
  const exclusions = offer ? (isWildfireOffer(offer) ? pickExclusions(offer) : pickOliveImportantTerms(offer)) : [];
  const directions = shouldOpenDirections(offer);

  const openWildfire = async () => {
    const merchantId = offer.wildfireMerchantId ?? offer.id;
    const win = openPending();
    let data = null;
    try {
      data = await apiService.trackWildfireClick(merchantId);
    } catch (error) {
      data = error?.response?.data || null;
    }
    const url = data?.success && normalizeHttpUrl(data.tracking_url);
    if (url) {
      finishOpen(win, url);
      return;
    }
    closePending(win);
    setMessage({ text: data?.error || 'Unable to load this offer. Please try again in a moment.' });
  };

  const openClick = async () => {
    const oliveId = pickOliveOfferId(offer);
    const merchant = oliveId ? { ...offer, offerId: oliveId } : offer;
    const offerId = merchant.offerId || merchant.id;
    const website = merchant.website ? normalizeHttpUrl(merchant.website) : null;
    if (!offerId) {
      if (website) window.open(website, '_blank', 'noopener,noreferrer');
      else setMessage({ text: `No website available for ${merchant.name}` });
      return;
    }
    const win = openPending();
    try {
      const response = await apiService.trackOfferClick(offerId);
      if (response?.success && response.tracking_url) {
        finishOpen(win, response.tracking_url);
      } else if (response && (response.error || response.success === false)) {
        closePending(win);
        if (website) {
          setMessage({
            text: response.details || 'Unable to track this offer, but you can still visit the store.',
            visitUrl: website,
          });
        } else {
          setMessage({ text: response.details || 'This offer is currently unavailable.' });
        }
      } else if (website) {
        finishOpen(win, website);
      } else {
        closePending(win);
        setMessage({ text: response?.details || 'Unable to open store website' });
      }
    } catch (_error) {
      if (website) {
        finishOpen(win, website);
      } else {
        closePending(win);
        setMessage({ text: 'Unable to open offer website' });
      }
    }
  };

  const handleShop = async () => {
    if (!offer || busy) return;
    setMessage(null);
    if (directions) {
      const query = pickMapsQuery(offer);
      if (!query) {
        setMessage({ text: 'Unable to open this store right now. Please try again.' });
        return;
      }
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`, '_blank', 'noopener,noreferrer');
      return;
    }
    setBusy(true);
    try {
      if (isWildfireOffer(offer)) await openWildfire();
      else if (isClickOffer(offer)) await openClick();
      else navigate('/shop', { state: { selectStoreName: name } });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="offer-page">
      <button type="button" className="offer-back" onClick={() => navigate(-1)}>
        <svg viewBox="0 0 8 14" aria-hidden="true">
          <path d="M6.5 1.5 1.5 7l5 5.5" />
        </svg>
        Back
      </button>
      {loading && !offer?.name ? (
        <p className="offer-empty">Loading shop...</p>
      ) : !offer || loadError ? (
        <p className="offer-empty">{loadError || 'Shop not found'}</p>
      ) : (
        <div className="offer-body">
          <div className="offer-hero">
            <span className="offer-logo">
              {logo ? <img src={logo} alt="" /> : <b>{initials(name) || '?'}</b>}
            </span>
            <h1>{name}</h1>
            {desc.full ? (
              <p className="offer-cats">
                {desc.needsExpand && !expanded ? desc.short : desc.full}
                {desc.needsExpand ? (
                  <button type="button" onClick={() => setExpanded((open) => !open)}>
                    {expanded ? ' less' : ' more'}
                  </button>
                ) : null}
              </p>
            ) : null}
            {isNewOffer(offer) ? <span className="offer-new">New</span> : null}
          </div>

          {isOlive ? (
            <div className="offer-card-notice">
              <span>Pay with a linked card to earn points</span>
              <Link to="/settings?tab=cards">{cardLinked ? 'Manage' : 'Link'}</Link>
            </div>
          ) : null}

          <div className="offer-tips">
            {isOlive ? (
              <div className="offer-tip">
                <span className="offer-tip-icon"><StoreIcon aria-hidden="true" /></span>
                <span className="offer-tip-copy">
                  <strong>Shopping in the app or store?</strong>
                  <span>Use your linked card to earn points.</span>
                </span>
              </div>
            ) : null}
            <div className="offer-tip">
              <span className="offer-tip-icon is-small"><XmarkIcon aria-hidden="true" /></span>
              <span className="offer-tip-copy">
                <strong>{exclusions.length ? 'Exclusions' : 'Some purchases won’t earn points'}</strong>
                {exclusions.length ? (
                  <ul>
                    {exclusions.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <span>Certain product categories and purchases using promo codes may not qualify.</span>
                )}
              </span>
            </div>
            {displayAddress ? (
              <div className="offer-tip">
                <span className="offer-tip-icon"><LocationIcon aria-hidden="true" /></span>
                <span className="offer-tip-copy">
                  <strong>Address</strong>
                  <span>{displayAddress}</span>
                </span>
              </div>
            ) : null}
          </div>

          {message ? (
            <div className="offer-message" role="alert">
              <span>{message.text}</span>
              {message.visitUrl ? (
                <a href={message.visitUrl} target="_blank" rel="noopener noreferrer" onClick={() => setMessage(null)}>
                  Visit Anyway
                </a>
              ) : null}
            </div>
          ) : null}

          <button type="button" className="offer-cta" onClick={handleShop} disabled={busy}>
            {busy ? 'Opening...' : directions ? 'Get directions' : 'Shop now'}
          </button>
        </div>
      )}
    </div>
  );
}

function OfferDetailGate() {
  if (!apiService.isAuthenticated()) return <TempStart />;
  return (
    <AppShell title="Shop">
      <OfferDetailPage />
    </AppShell>
  );
}

export default OfferDetailGate;
